import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import ConfirmDialog from '@/components/business/ConfirmDialog/ConfirmDialog';
import { catalogApi, type CatalogEntry } from '@/api/catalog';
import { jobsApi } from '@/api/jobs';
import { buildPartLabel } from '@/helpers/catalogParts';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import { useError } from '@/providers/ErrorProvider/ErrorProvider';
import { useJobs } from '@/providers/JobsProvider/JobsProvider';
import { useNewJob } from '@/providers/NewJobProvider/NewJobProvider';
import type { Job } from '@/types/types';
import NewJobCart from './components/NewJobCart/NewJobCart';
import NewJobForm from './components/NewJobForm/NewJobForm';
import NewServiceModal from './components/NewServiceModal/NewServiceModal';
import { styles } from './styles';

const norm = (v: string | null | undefined): string =>
    (v ?? '').trim().toLowerCase();

const findCatalogId = (
    catalog: CatalogEntry[],
    serviceType: string,
    repairType: string,
    partLabel: string,
): number | null => {
    const s = norm(serviceType);
    const r = norm(repairType);
    const pl = norm(partLabel);
    const entry = catalog.find(
        (c) =>
            norm(c.service_type) === s &&
            norm(c.repair_type) === r &&
            norm(buildPartLabel(c.part, c.position)) === pl,
    );
    return entry?.id ?? null;
};

const NewJobPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const newJob = useNewJob();
    const jobsCtx = useJobs();
    const errorCtx = useError();
    const companyCtx = useCompany();
    const isRepairer = companyCtx.state.company?.marketplace_side === 'Repairer';

    const [serviceModalOpen, setServiceModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmBackOpen, setConfirmBackOpen] = useState(false);
    const [catalog, setCatalog] = useState<CatalogEntry[]>([]);

    useEffect(() => {
        let cancelled = false;
        catalogApi
            .list()
            .then((res) => {
                if (!cancelled) setCatalog(res.data?.payload ?? []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    const isDirty = useMemo(() => {
        const s = newJob.state;
        return Boolean(
            s.location ||
                s.supplier ||
                s.vin ||
                s.color ||
                s.odometer ||
                s.stockRO ||
                s.poNumber ||
                s.services.length,
        );
    }, [newJob.state]);

    const doNavigateBack = () => {
        newJob.DISPATCH({ type: 'RESET_JOB' });
        navigate(-1);
    };

    const handleBack = () => {
        if (isDirty) {
            setConfirmBackOpen(true);
            return;
        }
        doNavigateBack();
    };

    const openAdd = () => {
        setEditingId(null);
        setServiceModalOpen(true);
    };

    const openEdit = (id: number) => {
        setEditingId(id);
        setServiceModalOpen(true);
    };

    const handleSubmit = async () => {
        const validLocation = Boolean(newJob.state.location?.id);
        const validVIN = Boolean(newJob.state.vin);
        const validColor = Boolean(newJob.state.color);
        const validSupplier = isRepairer ? Boolean(newJob.state.supplier?.id) : true;
        if (!validLocation || !validVIN || !validColor || !validSupplier) {
            errorCtx.DISPATCH({
                type: 'INVALID_INPUT',
                payload: { alert: 'Please fill all required fields' },
            });
            return;
        }
        if (!newJob.state.services.length) return;

        // Make sure the catalog is loaded before we try to resolve service ids.
        // The mount-time fetch may not have resolved yet on a fast submit, so
        // fall through to a synchronous load here.
        let activeCatalog = catalog;
        if (activeCatalog.length === 0) {
            try {
                const res = await catalogApi.list();
                activeCatalog = res.data?.payload ?? [];
                setCatalog(activeCatalog);
            } catch (e) {
                errorCtx.DISPATCH({ type: 'API_ERROR', payload: e as never });
                return;
            }
        }

        // Resolve every selected (service_type, repair_type, part, position)
        // tuple to a catalog service_details.id — the API requires that id
        // rather than the human-readable fields.
        const unresolved: string[] = [];
        const resolvedServices = newJob.state.services.flatMap((s) => {
            const partsList = s.parts.length > 0 ? s.parts : [''];
            return partsList.map((partName, i) => {
                const id = findCatalogId(activeCatalog, s.type, s.repair, partName);
                if (id == null) {
                    unresolved.push(
                        [s.type, s.repair, partName].filter(Boolean).join(' / '),
                    );
                }
                return {
                    service_details: { id: id ?? 0 },
                    repairer_company_id: s.repairer.id,
                    price: s.priceEach
                        ? parseFloat(s.prices[i] ?? s.price) || 0
                        : parseFloat(s.price) || 0,
                    quantity: 1,
                    media: s.media.map((m) => ({
                        media_type: m.media_type,
                        name: m.name,
                        text: m.text,
                    })),
                };
            });
        });

        if (unresolved.length > 0) {
            errorCtx.DISPATCH({
                type: 'INVALID_INPUT',
                payload: {
                    alert: `Could not match these services to the catalog: ${unresolved.join('; ')}`,
                },
            });
            return;
        }

        const payload = {
            location_id: newJob.state.location?.id,
            po_number: newJob.state.poNumber,
            ...(isRepairer && newJob.state.supplier?.id
                ? { supplier_company_id: newJob.state.supplier.id }
                : {}),
            vehicle: {
                vin: newJob.state.vin,
                color: newJob.state.color,
                odometer: newJob.state.odometer
                    ? { value: Number(newJob.state.odometer), unit: 'mi' }
                    : null,
                stock_number: newJob.state.stockRO,
            },
            services: resolvedServices,
        } as unknown as Partial<Job>;

        setSubmitting(true);
        try {
            const res = await jobsApi.create(payload);
            const created = res.data?.payload;
            newJob.DISPATCH({ type: 'RESET_JOB' });
            await jobsCtx.actions.loadJobs({});
            navigate(created?.id ? `/jobs?job=${created.id}` : '/jobs');
        } catch (e) {
            errorCtx.DISPATCH({ type: 'API_ERROR', payload: e as never });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={styles.page}>
            <Box sx={styles.topBar}>
                <Button
                    onClick={handleBack}
                    sx={styles.backBtn(theme)}
                    startIcon={<ArrowBackIcon />}
                >
                    Back
                </Button>
            </Box>
            <Box sx={styles.body}>
                <Box sx={styles.formColumn}>
                    <NewJobForm onAddService={openAdd} />
                </Box>
                <Box sx={styles.cartColumn}>
                    <NewJobCart
                        submitting={submitting}
                        onSubmit={handleSubmit}
                        onEditService={openEdit}
                    />
                </Box>
            </Box>
            <NewServiceModal
                open={serviceModalOpen}
                editingId={editingId}
                onClose={() => {
                    setServiceModalOpen(false);
                    setEditingId(null);
                }}
            />
            <ConfirmDialog
                open={confirmBackOpen}
                title="Discard changes?"
                message="You have unsaved changes to this job. Going back will discard them."
                confirmLabel="Discard"
                cancelLabel="Stay"
                danger
                onConfirm={() => {
                    setConfirmBackOpen(false);
                    doNavigateBack();
                }}
                onCancel={() => setConfirmBackOpen(false)}
            />
        </Box>
    );
};

export default NewJobPage;
